import { useEffect, useRef } from 'react';

interface Handlers<E> {
  onOpen?: () => void;
  onEvent?: (eventName: string, data: E) => void;
  onEnd?: (status: string) => void;
  onError?: (err: Event) => void;
}

/**
 * Wrapper React pour EventSource avec auto-cleanup.
 * Recrée la connexion à chaque changement d'url. Ferme à l'unmount.
 */
export function useEventSource<E = unknown>(
  url: string | null,
  handlers: Handlers<E>,
  events: string[] = ['window'],
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!url) return;
    const es = new EventSource(url, { withCredentials: true });

    es.addEventListener('open', () => handlersRef.current.onOpen?.());

    for (const name of events) {
      es.addEventListener(name, (ev) => {
        try {
          const data = JSON.parse((ev as MessageEvent).data) as E;
          handlersRef.current.onEvent?.(name, data);
        } catch {
          /* ignore */
        }
      });
    }

    es.addEventListener('end', (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as { status: string };
        handlersRef.current.onEnd?.(data.status);
      } catch {
        handlersRef.current.onEnd?.('unknown');
      }
      es.close();
    });

    es.onerror = (err) => handlersRef.current.onError?.(err);

    return () => {
      es.close();
    };
  }, [url, events.join('|')]);
}
